import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import Typography from 'app/ui/components/atoms/typography';
import Tooltip from 'app/ui/components/molecules/tooltip';
import QuestionMarkCircle from 'app/ui/components/atoms/icons/question-mark-circle';

import { TitleWithTooltip } from 'app/ui/components/molecules/tooltip/util';

const REST_DAY_BG_IMAGE = '/rest-day-bg.jpg';

export const isRestDay = (workoutId: string) => workoutId === 'rest-day' || workoutId.startsWith('rest-day-');

export type RestDayCardProps = {
  label?: string;
  testId?: string;
  backgroundImage?: string;
};

const CardContainer = styled.div`
  position: relative;
  width: 20rem;
  aspect-ratio: 420 / 260;
  border-radius: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.veryGray};
`;

const ImageBackground = styled.div<{ $backgroundImage?: string }>`
  position: absolute;
  inset: 0;
  background: url(${({ $backgroundImage }) => $backgroundImage || REST_DAY_BG_IMAGE}) center/cover;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 50%, rgba(0, 0, 0, 0.8) 100%);
  }
`;

const ContentOverlay = styled.div`
  position: absolute;
  inset: 0;
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 1;
`;

const LabelContainer = styled.div`
  align-self: flex-start;
  background: white;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.spacing(1)};
`;

const LabelText = styled(Typography)`
  text-transform: uppercase;
  font-weight: 600;
  font-size: ${({ theme }) => theme.typography.tiny};
`;

const Title = styled(Typography)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
`;

const RestDayCard: React.FC<RestDayCardProps> = ({ label, testId, backgroundImage }) => {
  const theme = useTheme();
  const intl = useIntl();

  const restDayTitle = intl.formatMessage({
    id: 'progressivePlan.restDay',
    defaultMessage: 'Rest day',
  });

  return (
    <CardContainer data-testid={testId}>
      <ImageBackground $backgroundImage={backgroundImage} />
      <ContentOverlay>
        {label ? (
          <LabelContainer>
            <LabelText variant="label" color={theme.colors.black}>
              {label}
            </LabelText>
          </LabelContainer>
        ) : (
          <div />
        )}
        <TitleWithTooltip>
          <Title variant="h5">{restDayTitle}</Title>
          <Tooltip tooltipText={intl.formatMessage({ id: 'progressivePlan.restDayTooltip' })} usePortal={true}>
            <QuestionMarkCircle />
          </Tooltip>
        </TitleWithTooltip>
      </ContentOverlay>
    </CardContainer>
  );
};

export default RestDayCard;
